import { defineStore } from 'pinia';
import {
  ENABLE_DEPOSIT_BONUS_CAMPAIGN,
  hasCompletedDepositBonusLadder,
} from '@/domain/deposit/deposit-bonus-campaign';

/** 关闭福利弹窗后，未发生充值操作则再次弹出 */
export const DEPOSIT_BONUS_REMIND_MS = 3 * 60 * 1000;

let remindTimer = 0;
let eligibilityRequest = null;

function clearRemindTimer() {
  if (remindTimer && typeof window !== 'undefined') {
    window.clearTimeout(remindTimer);
  }
  remindTimer = 0;
}

/**
 * 充值赠送福利弹窗：游客关闭注册奖励弹窗后展示一次；登录后关闭则 3 分钟未充值再提醒。
 */
export const useDepositBonusPromptStore = defineStore('depositBonusPrompt', {
  state: () => ({
    dialogOpen: false,
    /** 本次登录会话内可自动弹出 / 提醒 */
    sessionActive: false,
    /** 登录后尚未完成首次自动弹出 */
    armed: false,
    /** 游客关闭注册奖励弹窗后，尚未完成一次活动展示 */
    guestArmed: false,
    /** 当前游客会话已展示过活动弹窗 */
    guestShown: false,
    /** 3 分钟到点，等前置弹窗让出后再弹 */
    pendingRemind: false,
    /** 已发生充值操作（下单 / 成功），不再自动弹 */
    acted: false,
    /** 未领完最高阶梯奖励时展示活动条 / 弹窗（与是否曾充值无关） */
    campaignEligible: false,
    eligibilityResolved: false,
    eligibilityLoading: false,
    eligibilityError: '',
    eligibilityGeneration: 0,
    /** 历史最高真实入账 USD（进度条 / 资格：仅接口，不含本笔输入） */
    maxCreditedUsd: 0,
    /** 活动参与接口 GET /liyuan-trade/activity/participation 数据 */
    participationData: null,
    /** 充值弹窗本笔预估 USD（仅用于「本笔送」预览，不抬高进度） */
    ladderPreviewUsd: 0,
  }),

  getters: {
    /** 进度条 / 福利窗当前进度：只信真实入账 */
    ladderProgressUsd(state) {
      return Math.max(0, Number(state.maxCreditedUsd) || 0);
    },
    /** 任意 UI 入口：总开关关则永不展示 */
    campaignVisible(state) {
      return ENABLE_DEPOSIT_BONUS_CAMPAIGN && state.campaignEligible;
    },
  },

  actions: {
    setLadderPreviewUsd(usd) {
      const n = Number(usd);
      this.ladderPreviewUsd = Number.isFinite(n) && n > 0 ? n : 0;
    },

    resetOnLogout() {
      this.dialogOpen = false;
      this.sessionActive = false;
      this.armed = false;
      this.guestArmed = false;
      this.guestShown = false;
      this.pendingRemind = false;
      this.acted = false;
      this.campaignEligible = false;
      this.eligibilityResolved = false;
      this.eligibilityLoading = false;
      this.eligibilityError = '';
      this.maxCreditedUsd = 0;
      this.participationData = null;
      this.ladderPreviewUsd = 0;
      this.eligibilityGeneration += 1;
      eligibilityRequest = null;
      clearRemindTimer();
    },

    applyCreditedUsd(maxCreditedUsd) {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN) {
        this.campaignEligible = false;
        this.eligibilityResolved = true;
        return false;
      }
      const amount = Math.max(0, Number(maxCreditedUsd) || 0);
      this.maxCreditedUsd = amount;
      this.campaignEligible = !hasCompletedDepositBonusLadder(amount);
      this.eligibilityResolved = true;
      this.eligibilityError = '';
      if (!this.campaignEligible) this.markActed();
      return this.campaignEligible;
    },

    /**
     * 活动条 / 弹窗资格：未达最高阶梯门槛则展示（已充值用户仍可展示）。
     * 数据源：充值订单 CREDITED 最高入账 → 失败回退 withdraw-conditions.depositAmount。
     * @param {object|null} [knownConditions] 保留参数以兼容旧调用；优先走订单接口
     * @param {{ force?: boolean }} [opts]
     */
    async ensureEligibility(knownConditions = null, opts = {}) {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN) {
        this.campaignEligible = false;
        this.eligibilityResolved = true;
        return false;
      }
      if (opts.force) {
        this.eligibilityResolved = false;
        eligibilityRequest = null;
      }
      if (this.eligibilityResolved) return this.campaignEligible;
      if (eligibilityRequest) return eligibilityRequest;

      const generation = this.eligibilityGeneration;
      this.eligibilityLoading = true;
      const request = (async () => {
        try {
          const { fetchDepositBonusCreditedUsd, fetchActivityParticipation } = await import(
            '@/api/deposit-bonus'
          );
          try {
            const part = await fetchActivityParticipation(8);
            if (generation === this.eligibilityGeneration && part) {
              this.participationData = part;
              if (
                part.sealed ||
                (part.tierClaimMode === 'HIGHEST_TIER_ONLY' && part.participated)
              ) {
                this.campaignEligible = false;
                this.eligibilityResolved = true;
                return false;
              }
            }
          } catch (e) {
            /* ignore participation error, fallback to orders */
          }
          const result = await fetchDepositBonusCreditedUsd({ force: Boolean(opts.force) });
          if (generation !== this.eligibilityGeneration) return false;

          if (!result.unknown) {
            return this.applyCreditedUsd(result.maxCreditedUsd);
          }

          const depositAmount = Number(knownConditions?.depositAmount);
          if (Number.isFinite(depositAmount) && depositAmount >= 0) {
            return this.applyCreditedUsd(depositAmount);
          }

          throw new Error('missing credited deposit amount');
        } catch (err) {
          if (generation === this.eligibilityGeneration) {
            this.campaignEligible = false;
            this.eligibilityError = String(err?.message ?? 'eligibility_failed');
          }
          return false;
        } finally {
          if (eligibilityRequest === request) eligibilityRequest = null;
          if (generation === this.eligibilityGeneration) this.eligibilityLoading = false;
        }
      })();
      eligibilityRequest = request;
      return request;
    },

    /** 登录成功后武装；等前置弹窗结束再 tryShow */
    armAfterLogin() {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN || !this.campaignEligible) return false;
      if (!this.campaignEligible) return false;
      this.sessionActive = true;
      this.armed = true;
      this.pendingRemind = false;
      this.acted = false;
      this.dialogOpen = false;
      clearRemindTimer();
      return true;
    },

    armGuestAfterRegisterRewardDismiss() {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN || this.guestShown || this.dialogOpen) return false;
      if (this.guestShown || this.dialogOpen) return false;
      this.guestArmed = true;
      return true;
    },

    tryShowGuest(opts = {}) {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN || !this.guestArmed || this.guestShown || this.dialogOpen)
        return false;
      if (!this.guestArmed || this.guestShown || this.dialogOpen) return false;
      if (opts.blocked) return false;
      this.guestArmed = false;
      this.guestShown = true;
      this.dialogOpen = true;
      return true;
    },

    hideForQuietShell() {
      this.dialogOpen = false;
    },

    /** 活动条等手动打开 */
    openManual() {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN || !this.campaignEligible) return false;
      if (!this.campaignEligible) return false;
      this.dialogOpen = true;
      this.pendingRemind = false;
      clearRemindTimer();
      return true;
    },

    /**
     * @param {{ blocked?: boolean }} [opts]
     */
    tryShow(opts = {}) {
      if (
        !ENABLE_DEPOSIT_BONUS_CAMPAIGN ||
        !this.campaignEligible ||
        !this.sessionActive ||
        this.acted ||
        this.dialogOpen
      ) {
        return false;
      }
      if (opts.blocked) return false;
      if (!this.armed && !this.pendingRemind) return false;
      this.armed = false;
      this.pendingRemind = false;
      this.dialogOpen = true;
      clearRemindTimer();
      return true;
    },

    /** 用户关掉弹窗（X / 遮罩）；未充值则预约 3 分钟提醒 */
    dismiss() {
      if (!this.dialogOpen) return;
      this.dialogOpen = false;
      this.scheduleRemind();
    },

    /** 「去充值」：关掉福利窗并预约提醒（打开充值弹窗本身不算充值操作）。 */
    closeBeforeDeposit() {
      this.dialogOpen = false;
      this.scheduleRemind();
    },

    scheduleRemind() {
      clearRemindTimer();
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN) return;
      if (!this.sessionActive || this.acted) return;
      if (typeof window === 'undefined') return;
      remindTimer = window.setTimeout(() => {
        remindTimer = 0;
        if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN || !this.sessionActive || this.acted || this.dialogOpen)
          return;
        this.pendingRemind = true;
      }, DEPOSIT_BONUS_REMIND_MS);
    },

    stopRemind() {
      clearRemindTimer();
    },

    markActed() {
      this.acted = true;
      this.armed = false;
      this.guestArmed = false;
      this.pendingRemind = false;
      this.dialogOpen = false;
      clearRemindTimer();
    },

    markSuccessfulDeposit(payload = {}) {
      if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN) {
        this.campaignEligible = false;
        this.eligibilityResolved = true;
        this.eligibilityLoading = false;
        this.eligibilityError = '';
        this.markActed();
        return;
      }

      const amount = Number(payload?.amount);
      const credited = Number.isFinite(amount) && amount > 0 ? amount : 0;
      this.maxCreditedUsd = Math.max(this.maxCreditedUsd, credited);
      this.eligibilityGeneration += 1;
      eligibilityRequest = null;

      try {
        import('@/utils/deposit-real-credit').then((m) => m.invalidateRealDepositCreditCache());
      } catch {
        /* ignore */
      }

      if (hasCompletedDepositBonusLadder(this.maxCreditedUsd)) {
        this.campaignEligible = false;
        this.eligibilityResolved = true;
        this.eligibilityLoading = false;
        this.eligibilityError = '';
        this.markActed();
        return;
      }

      // 未达最高档：保持活动展示，停止本轮自动提醒即可
      this.campaignEligible = true;
      this.eligibilityResolved = true;
      this.eligibilityLoading = false;
      this.eligibilityError = '';
      this.acted = true;
      this.armed = false;
      this.pendingRemind = false;
      this.dialogOpen = false;
      clearRemindTimer();
    },
  },
});

export function markDepositBonusActed() {
  try {
    useDepositBonusPromptStore().markActed();
  } catch {
    /* Pinia 未就绪 */
  }
}

export function markDepositBonusSuccessful(payload) {
  try {
    useDepositBonusPromptStore().markSuccessfulDeposit(payload);
  } catch {
    /* Pinia 未就绪 */
  }
}
